import React from 'react';

export default function Privacy() {
  const sections = [
    {
      number: '01',
      title: 'Introduction',
      content: `Family Ledger ("we", "us", or "our") is a personal household budgeting application available at schirmerledger.app. This Privacy Policy describes how we collect, use, and protect your personal and financial information when you use our application. By using Family Ledger, you agree to the collection and use of information in accordance with this policy.`
    },
    {
      number: '02',
      title: 'Information We Collect',
      subsections: [
        {
          title: 'Account Information',
          content: 'When you create an account, we collect your name and email address. If you sign in with Google, we receive your name and email from Google OAuth.'
        },
        {
          title: 'Financial Data',
          content: 'With your explicit consent through Plaid Link, we access your bank account balances and transaction history from your financial institutions. This data is used solely to provide budgeting and financial tracking features within the application. We do not sell, share, or use your financial data for any purpose other than displaying it to you within the app.'
        },
        {
          title: 'Usage Data',
          content: 'We may log basic usage information such as login timestamps for security and debugging purposes.'
        }
      ]
    },
    {
      number: '03',
      title: 'How We Use Your Information',
      bullets: [
        'Provide and maintain the budgeting application',
        'Display your account balances and transaction history',
        'Calculate budget summaries and spending reports',
        'Sync data with Google Drive if you enable that feature',
        'Authenticate your identity and maintain session security'
      ]
    },
    {
      number: '04',
      title: 'Data Sharing',
      content: `We do not sell, trade, or share your personal or financial information with third parties, except as strictly necessary to provide the service — specifically Plaid for financial data access, and Google for OAuth authentication and Drive sync. We do not display advertisements and do not allow advertisers access to your data.`
    },
    {
      number: '05',
      title: 'Data Security',
      content: `We implement industry-standard security measures to protect your data, including HTTPS/TLS encryption for all data in transit (enforced through nginx with Let's Encrypt certificates), SSH key-based server authentication, and restricted database access limited to localhost only. Sensitive credentials are stored as environment variables and never exposed in source code.`
    },
    {
      number: '06',
      title: 'Data Retention & Deletion',
      content: `Your data is retained for as long as your account is active. You may request deletion of your account and all associated data at any time by contacting us at maverick.schirmer@gmail.com. Upon request, we will permanently delete your personal information and financial data from our systems within 30 days.`
    },
    {
      number: '07',
      title: 'Your Rights',
      bullets: [
        'Access or correct your personal information at any time within the app',
        'Request complete deletion of your account and data',
        'Revoke Plaid\'s access to your financial accounts at any time via my.plaid.com',
        'Disconnect your Google account from the application settings'
      ]
    },
    {
      number: '08',
      title: 'Contact',
      content: `If you have questions or concerns about this Privacy Policy or how your data is handled, please contact us at maverick.schirmer@gmail.com. We will respond to all privacy-related inquiries within 5 business days.`
    }
  ];

  return (
    <div style={{
      minHeight: '100vh',
      background: '#0a0e1a',
      fontFamily: "'DM Sans', -apple-system, sans-serif",
      color: '#e2e8f0',
      padding: '0',
    }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600&family=DM+Mono:wght@400;500&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }
        .privacy-link { color: #3b82f6; text-decoration: none; }
        .privacy-link:hover { text-decoration: underline; }
        .section-card {
          background: rgba(255,255,255,0.03);
          border: 1px solid rgba(255,255,255,0.07);
          border-radius: 12px;
          padding: 32px;
          margin-bottom: 16px;
          transition: border-color 0.2s;
        }
        .section-card:hover { border-color: rgba(59,130,246,0.25); }
        .bullet-item {
          display: flex;
          align-items: flex-start;
          gap: 12px;
          padding: 8px 0;
          color: #94a3b8;
          font-size: 15px;
          line-height: 1.6;
        }
        .bullet-dot {
          width: 6px;
          height: 6px;
          border-radius: 50%;
          background: #3b82f6;
          flex-shrink: 0;
          margin-top: 8px;
        }
        .subsection { margin-top: 20px; }
        .subsection:first-child { margin-top: 0; }
        .subsection-title {
          font-size: 13px;
          font-weight: 500;
          color: #10b981;
          text-transform: uppercase;
          letter-spacing: 0.08em;
          margin-bottom: 8px;
          font-family: "'DM Mono', monospace";
        }
      `}</style>

      {/* Header */}
      <div style={{
        borderBottom: '1px solid rgba(255,255,255,0.07)',
        background: 'rgba(255,255,255,0.02)',
      }}>
        <div style={{ maxWidth: 720, margin: '0 auto', padding: '20px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <a href="/" style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{
              width: 32, height: 32, borderRadius: 8,
              background: 'linear-gradient(135deg, #1e3a5f 0%, #0a0e1a 100%)',
              border: '1px solid rgba(59,130,246,0.4)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 13, fontWeight: 700, color: '#3b82f6', letterSpacing: '-0.5px'
            }}>FL</div>
            <span style={{ color: '#e2e8f0', fontWeight: 600, fontSize: 15 }}>Family Ledger</span>
          </a>
          <a href="/" style={{ color: '#64748b', fontSize: 14, textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 6 }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M19 12H5M12 5l-7 7 7 7"/></svg>
            Back to app
          </a>
        </div>
      </div>

      {/* Hero */}
      <div style={{ maxWidth: 720, margin: '0 auto', padding: '56px 24px 40px' }}>
        <div style={{
          display: 'inline-flex', alignItems: 'center', gap: 8,
          background: 'rgba(59,130,246,0.1)', border: '1px solid rgba(59,130,246,0.2)',
          borderRadius: 20, padding: '5px 14px', marginBottom: 20
        }}>
          <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#3b82f6' }} />
          <span style={{ fontSize: 12, color: '#3b82f6', fontWeight: 500, letterSpacing: '0.05em', textTransform: 'uppercase' }}>Privacy Policy</span>
        </div>

        <h1 style={{
          fontSize: 'clamp(28px, 5vw, 40px)',
          fontWeight: 600,
          color: '#f1f5f9',
          lineHeight: 1.2,
          marginBottom: 16,
          letterSpacing: '-0.02em'
        }}>
          Your data,<br />
          <span style={{ color: '#3b82f6' }}>your control.</span>
        </h1>

        <p style={{ color: '#64748b', fontSize: 15, lineHeight: 1.7, maxWidth: 520 }}>
          Family Ledger is a private household budgeting app. We only collect what's needed to show you your finances — nothing more.
        </p>

        <div style={{
          display: 'flex', gap: 16, marginTop: 28, flexWrap: 'wrap'
        }}>
          {[
            { icon: '🔒', label: 'HTTPS encrypted' },
            { icon: '🚫', label: 'No ads, ever' },
            { icon: '🗑️', label: 'Delete anytime' },
          ].map(item => (
            <div key={item.label} style={{
              display: 'flex', alignItems: 'center', gap: 8,
              background: 'rgba(255,255,255,0.04)',
              border: '1px solid rgba(255,255,255,0.07)',
              borderRadius: 8, padding: '8px 14px',
              fontSize: 13, color: '#94a3b8'
            }}>
              <span>{item.icon}</span>
              <span>{item.label}</span>
            </div>
          ))}
        </div>

        <div style={{ marginTop: 12, fontSize: 13, color: '#475569' }}>
          Last updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
        </div>
      </div>

      {/* Sections */}
      <div style={{ maxWidth: 720, margin: '0 auto', padding: '0 24px 80px' }}>
        {sections.map((section) => (
          <div key={section.number} className="section-card">
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16, marginBottom: section.subsections || section.bullets ? 20 : 12 }}>
              <span style={{
                fontFamily: 'DM Mono, monospace',
                fontSize: 12, color: '#3b82f6', fontWeight: 500,
                background: 'rgba(59,130,246,0.1)', border: '1px solid rgba(59,130,246,0.2)',
                borderRadius: 6, padding: '3px 8px', flexShrink: 0, marginTop: 2
              }}>{section.number}</span>
              <h2 style={{ fontSize: 18, fontWeight: 600, color: '#f1f5f9', letterSpacing: '-0.01em' }}>
                {section.title}
              </h2>
            </div>

            {section.content && (
              <p style={{ color: '#94a3b8', fontSize: 15, lineHeight: 1.75, paddingLeft: 48 }}>
                {section.content}
              </p>
            )}

            {section.bullets && (
              <div style={{ paddingLeft: 48 }}>
                {section.bullets.map((b, i) => (
                  <div key={i} className="bullet-item">
                    <div className="bullet-dot" />
                    <span>{b}</span>
                  </div>
                ))}
              </div>
            )}

            {section.subsections && (
              <div style={{ paddingLeft: 48 }}>
                {section.subsections.map((sub, i) => (
                  <div key={i} className="subsection" style={{ marginTop: i === 0 ? 0 : 20 }}>
                    <div className="subsection-title">{sub.title}</div>
                    <p style={{ color: '#94a3b8', fontSize: 15, lineHeight: 1.75 }}>{sub.content}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}

        {/* Footer */}
        <div style={{
          marginTop: 40, padding: '24px', borderRadius: 12,
          background: 'rgba(59,130,246,0.05)',
          border: '1px solid rgba(59,130,246,0.15)',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16
        }}>
          <div>
            <div style={{ fontSize: 14, fontWeight: 500, color: '#e2e8f0', marginBottom: 4 }}>Questions about your privacy?</div>
            <div style={{ fontSize: 13, color: '#64748b' }}>We'll get back to you within 5 business days.</div>
          </div>
          <a href="mailto:maverick.schirmer@gmail.com" className="privacy-link" style={{
            display: 'inline-flex', alignItems: 'center', gap: 8,
            background: '#3b82f6', color: 'white', textDecoration: 'none',
            padding: '10px 18px', borderRadius: 8, fontSize: 14, fontWeight: 500
          }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
            Contact us
          </a>
        </div>
      </div>
    </div>
  );
}
